import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  useTheme,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  LuFileDown,
  LuFileText,
  LuChevronRight,
  LuInfo,
  LuLayers,
  LuClipboardList,
  LuGauge,
  LuBookOpen,
  LuChartColumn,
  LuDownload,
  LuFile,
} from "react-icons/lu";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import { TableHeaderContainer } from "../container/TableHeaderContainer";
import { TableTitleContainer } from "../container/TableTitleContainer";
import { LifeCycleAssessment as LCA } from "../../db_access/life_cycle_assessment";
import { EnvFactor } from "../../db_access/env_factor";
import { exportWorkspaceAsDocx } from "../../utils/docxUtils/workspaceExportAsDocx";
import AgGrid from "../grid/AgGrid";
import { defaultColDef, leftAlignColDef } from "../grid/columnDefinitions/columnDefinitions";
import config from "../../config";
import arivuLogo from "../../assets/logo/ArivuEN.png";

// Mapped Workspace results components
import Footprint from "../../environment-page-components/Footprint";
import SystemBoundary from "../../environment-page-components/SystemBoundary";
import { Product } from "../../db_access/product";
import { Project } from "../../db_access/project";

const GREEN = "#15a448";

// Link renderer for files or URLs in AgGrid
const LinkRenderer = (params) => {
  if (!params.value || params.value === "N/A") return <span>N/A</span>;
  return (
    <a
      href={params.value}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: GREEN, textDecoration: "underline", fontWeight: 500 }}
    >
      {params.value.substring(0, 45) + (params.value.length > 45 ? "..." : "")}
    </a>
  );
};

// Standard KVTable using AgGrid with negative horizontal margin to clear padding
function KVTable({ rows, isLinkColumn = (field) => false }) {
  const { t } = useTranslation();
  const colDefs = useMemo(() => [
    {
      headerName: t("Attribute"),
      field: "field",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 200,
      flex: 1,
      cellStyle: {
        ...leftAlignColDef.cellStyle,
        fontSize: "14px",
        fontFamily: "Poppins, sans-serif",
      },
    },
    {
      headerName: t("Value"),
      field: "value",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 220,
      flex: 2,
      cellStyle: {
        ...leftAlignColDef.cellStyle,
        fontSize: "14px",
        fontFamily: "Poppins, sans-serif",
      },
      cellRenderer: (params) => {
        if (isLinkColumn(params.data.field)) {
          return <LinkRenderer {...params} />;
        }
        return params.value;
      }
    },
  ], [t, isLinkColumn]);

  const rowData = useMemo(() => rows.map(([k, v], i) => ({ id: i, field: k, value: v })), [rows]);

  return (
    <Box sx={{ my: 1, mx: -3 }}>
      <AgGrid
        columnDefs={colDefs}
        rowData={rowData}
        getRowId={(params) => String(params.data.id)}
        domLayout="autoHeight"
        suppressPagination
        headerCheckbox={false}
        checkboxes={false}
      />
    </Box>
  );
}

// Stand-in for reference's PanelCard
function PanelCard({ title, children, tok }) {
  return (
    <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: tok.radius.card, overflow: "hidden", alignSelf: "start" }}>
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title" sx={{ fontFamily: tok.font.family }}>{title}</Typography>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

const WorkspaceReportPanel = ({ entity, type, processes = [], envfactors = [], onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [selected, setSelected] = useState("overview");
  const [fullMode, setFullMode] = useState(false);
  const [lcaInfo, setLcaInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localEnvFactors, setLocalEnvFactors] = useState([]);

  // File blob Object URLs
  const [photoUrl, setPhotoUrl] = useState(null);
  const [specDocUrl, setSpecDocUrl] = useState(null);
  const [safetyDocUrl, setSafetyDocUrl] = useState(null);

  // Fetch document files if type is Product
  useEffect(() => {
    let pUrl = null;
    let sUrl = null;
    let sfUrl = null;

    if (type === "Product" && entity?.id) {
      Product.get_photo(entity.id)
        .then((res) => res.ok ? res.blob() : null)
        .then((blob) => {
          if (blob) {
            pUrl = URL.createObjectURL(blob);
            setPhotoUrl(pUrl);
          }
        })
        .catch(() => {});

      Product.get_spec_document(entity.id)
        .then((res) => res.ok ? res.blob() : null)
        .then((blob) => {
          if (blob) {
            sUrl = URL.createObjectURL(blob);
            setSpecDocUrl(sUrl);
          }
        })
        .catch(() => {});

      Product.get_safety_document(entity.id)
        .then((res) => res.ok ? res.blob() : null)
        .then((blob) => {
          if (blob) {
            sfUrl = URL.createObjectURL(blob);
            setSafetyDocUrl(sfUrl);
          }
        })
        .catch(() => {});
    }

    return () => {
      if (pUrl) URL.revokeObjectURL(pUrl);
      if (sUrl) URL.revokeObjectURL(sUrl);
      if (sfUrl) URL.revokeObjectURL(sfUrl);
    };
  }, [entity?.id, type]);

  useEffect(() => {
    if (envfactors && envfactors.length > 0) {
      setLocalEnvFactors(envfactors);
      return;
    }
    if (!processes || processes.length === 0) {
      setLocalEnvFactors([]);
      return;
    }
    EnvFactor.get_by_process_ids(processes.map((p) => p.id))
      .then((res) => res.json())
      .then((data) => setLocalEnvFactors(data || []))
      .catch(() => setLocalEnvFactors([]));
  }, [processes, envfactors]);

  // Fetch LCA data
  useEffect(() => {
    if (!entity?.id) return;

    setLoading(true);
    const fetchLca = type === "Product"
      ? LCA.get_by_product_id(entity.id)
      : LCA.get_by_project_id(entity.id);

    fetchLca
      .then(r => r.json())
      .then((lcaData) => {
        if (lcaData && lcaData.length > 0) {
          setLcaInfo(lcaData[0]);
        } else {
          setLcaInfo(null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [entity?.id, type]);

  // Section configs
  const sectionList = [
    { key: "overview", label: "Entity Overview", icon: LuInfo, blurb: "Product/Project details and general metadata.", status: "complete" },
    { key: "scope", label: "Scope & Goals", icon: LuClipboardList, blurb: "Scope boundaries, roles, temporal boundaries, and cut-off rules.", status: "complete" },
    { key: "lci", label: "Inventory (LCI) Processes", icon: LuLayers, blurb: "List of inventory processes mapped across stages.", status: processes.length > 0 ? "complete" : "partial" },
    { key: "env_factors", label: "Environmental Factors", icon: LuLayers, blurb: "Details of environmental factors mapped under categories.", status: localEnvFactors.length > 0 ? "complete" : "partial" },
    { key: "results", label: "Results & Footprints", icon: LuChartColumn, blurb: "Environmental footprint charts and calculated GWP indicators.", status: processes.length > 0 ? "complete" : "partial" },
  ];

  // Process footprint details
  const footprint = useMemo(() => {
    let fossilGwp = 0;
    let biogenicGwp = 0;
    let netGwp = 0;

    processes.forEach((p) => {
      const gwpVal = parseFloat(p.gwp) || 0;
      const bioVal = parseFloat(p.biogenic_gwp) || 0;
      fossilGwp += gwpVal;
      biogenicGwp += bioVal;
      netGwp += (gwpVal + bioVal);
    });

    return {
      fossilGwp,
      biogenicGwp,
      netGwp,
    };
  }, [processes]);

  // Read-only Process grid columns
  const processGridColDefs = useMemo(() => [
    {
      headerName: t("LCA Stage"),
      field: "lca_stage",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 150,
      flex: 1,
      cellStyle: { fontSize: "14px", fontFamily: "Poppins, sans-serif" },
    },
    {
      headerName: t("Process Name"),
      field: "name",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 200,
      flex: 2,
      cellStyle: { fontSize: "14px", fontFamily: "Poppins, sans-serif" },
    },
    {
      headerName: t("GWP Total (kg CO₂e)"),
      field: "gwp",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 120,
      flex: 1,
      cellStyle: { fontSize: "14px", fontFamily: "Poppins, sans-serif" },
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : "0.00",
    },
    {
      headerName: t("Biogenic GWP Total (kg CO₂e)"),
      field: "biogenic_gwp",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 150,
      flex: 1,
      cellStyle: { fontSize: "14px", fontFamily: "Poppins, sans-serif" },
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : "0.00",
    },
  ], [t]);

  // Environmental factors table columns
  const envFactorColDefs = useMemo(() => [
    {
      headerName: t("Category"),
      field: "category",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 150,
      flex: 1,
      cellStyle: { fontSize: "14px", fontFamily: "Poppins, sans-serif" },
    },
    {
      headerName: t("Sub-Category"),
      field: "sub_category",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 150,
      flex: 1,
      cellStyle: { fontSize: "14px", fontFamily: "Poppins, sans-serif" },
    },
    {
      headerName: t("Quantity"),
      field: "quantity",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 100,
      flex: 1,
      cellStyle: { fontSize: "14px", fontFamily: "Poppins, sans-serif" },
    },
    {
      headerName: t("Unit"),
      field: "unit",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 100,
      flex: 1,
      cellStyle: { fontSize: "14px", fontFamily: "Poppins, sans-serif" },
    },
    {
      headerName: t("GWP"),
      field: "gwp_total",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 100,
      flex: 1,
      cellStyle: { fontSize: "14px", fontFamily: "Poppins, sans-serif" },
      valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(2) : "0.00",
    },
  ], [t]);

  const isOverviewLink = (field) => {
    return [
      t("Supplier Website")
    ].includes(field);
  };

  const isScopeLink = (field) => {
    return [
      t("EPD Document Link"),
      t("PCR Document Link")
    ].includes(field);
  };

  // DOCX Export Handler
  const handleDocxExport = async () => {
    setLoading(true);
    try {
      // Fetch the logo image and convert it to base64
      let logoBase64 = null;
      try {
        const logoRes = await fetch(arivuLogo);
        const logoBlob = await logoRes.blob();
        logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
      } catch (e) {
        console.error("Failed to load logo image:", e);
      }

      const today = new Date().toLocaleDateString("en-CA");
      await exportWorkspaceAsDocx({
        entityName: entity?.name || "Workspace",
        entityCode: entity?.uuid || entity?.code || "N/A",
        entityType: type,
        today,
        metadata: {
          category: entity?.category,
          sub_category: entity?.sub_category,
          organizationName: entity?.organization_name || entity?.company_name || "N/A",
          placeOfManufacture: entity?.place_of_manufacture || "N/A",
          serviceLife: entity?.service_life != null ? `${entity.service_life} years` : "N/A",
          extendedServiceLife: entity?.extended_service_life != null ? `${entity.extended_service_life} years` : "N/A",
          specifications: entity?.specifications || "N/A",
          contactPerson: entity?.detail_contact_person || "N/A",
          contactEmail: entity?.detail_email || "N/A",
          contactPhone: entity?.detail_phone || "N/A",
          supplierAddress: entity?.detail_address || "N/A",
          supplierWebsite: entity?.website || "N/A",
        },
        scopeData: {
          role: entity?.role || "LCA Assessor",
          boundary: entity?.org || "Operational control",
          temporal: `${entity?.start || today} to ${entity?.end || today}`,
          cutoff: lcaInfo?.cutoff || "1% residual threshold by mass",
          boundary_statement: entity?.system_boundary || `This LCA assessment is prepared by the manufacturer under operational-control organizational boundary applying ISO 14040/44 guidelines.`,
          goals: entity?.goals || "N/A",
          assumptions: entity?.assumptions || "N/A",
          pcrLink: entity?.pcr_link || "N/A",
          epdLink: entity?.epd_link || "N/A",
        },
        lcaData: {
          declared_unit: lcaInfo?.declared_unit || entity?.functional_unit || "1 unit",
          reference_pcr: lcaInfo?.reference_pcr || entity?.pcr_link || "N/A",
          lca_software: lcaInfo?.lca_software || "N/A",
          lci_database: lcaInfo?.lci_database || "N/A",
          lcia_methodology: lcaInfo?.lcia_methodology || "N/A",
          cutoff: lcaInfo?.cutoff || "N/A",
          allocation_procedure: lcaInfo?.allocation_procedure || "N/A",
        },
        processes,
        footprintData: footprint,
        envFactors: localEnvFactors,
        logoBase64,
      });
    } catch (e) {
      console.error("Export failure:", e);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (secKey) => {
    switch (secKey) {
      case "overview":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Grid 1: Product / Project Info */}
            <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: "16px", p: 3, backgroundColor: tok.bg.card }}>
              <Typography variant="h6" sx={{ fontFamily: tok.font.family, fontWeight: 600, color: tok.text.primary, mb: 2 }}>
                {type === "Product" ? t("Product Information") : t("Project Information")}
              </Typography>
              <KVTable
                isLinkColumn={isOverviewLink}
                rows={[
                  [t("Product name"), entity?.name || "N/A"],
                  [t("Product ID"), entity?.uuid || entity?.code || "N/A"],
                  [t("Product category"), entity?.category || "N/A"],
                  [t("Sub-category"), entity?.sub_category || "N/A"],
                  [t("Entity Type"), type],
                  [t("Dimension / Specifications"), entity?.specifications || "N/A"],
                  [t("Reference Service Life"), entity?.service_life != null ? `${entity.service_life} years` : "N/A"],
                  [t("Extended Service Life"), entity?.extended_service_life != null ? `${entity.extended_service_life} years` : "N/A"],
                  [t("Place of Manufacture"), entity?.place_of_manufacture || "N/A"],
                ]}
              />
            </Paper>

            {/* Grid 2: Supplier Info */}
            <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: "16px", p: 3, backgroundColor: tok.bg.card }}>
              <Typography variant="h6" sx={{ fontFamily: tok.font.family, fontWeight: 600, color: tok.text.primary, mb: 2 }}>
                {t("Supplier & Organization Details")}
              </Typography>
              <KVTable
                isLinkColumn={isOverviewLink}
                rows={[
                  [t("Supplier / Organization Name"), entity?.organization_name || entity?.company_name || "N/A"],
                  [t("Contact Person"), entity?.detail_contact_person || "N/A"],
                  [t("Contact Email"), entity?.detail_email || "N/A"],
                  [t("Contact Phone"), entity?.detail_phone || "N/A"],
                  [t("Supplier Address"), entity?.detail_address || "N/A"],
                  [t("Supplier Website"), entity?.website || "N/A"],
                ]}
              />
            </Paper>

            {/* Grid 3: Documentation & Media (Excludes raw blob strings from tables) */}
            {(photoUrl || specDocUrl || safetyDocUrl) && (
              <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: "16px", p: 3, backgroundColor: tok.bg.card }}>
                <Typography variant="h6" sx={{ fontFamily: tok.font.family, fontWeight: 600, color: tok.text.primary, mb: 2 }}>
                  {t("Media & Compliance Documentation")}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, mt: 2 }}>
                  {photoUrl && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: tok.text.muted }}>{t("Product Photo Preview")}</Typography>
                      <img src={photoUrl} alt="Product Preview" style={{ width: "160px", height: "160px", objectFit: "cover", borderRadius: "8px", border: `1px solid ${tok.border.card}` }} />
                    </Box>
                  )}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 200, justifyContent: "center" }}>
                    {specDocUrl && (
                      <Button variant="outlined" startIcon={<LuFile size={18} />} endIcon={<LuDownload size={16} />} href={specDocUrl} target="_blank" download="spec_document" sx={{
                        textTransform: "none", borderColor: GREEN, color: GREEN, "&:hover": { borderColor: "#118c3c", color: "#118c3c" }, borderRadius: "8px", py: 1.25, justifyContent: "space-between", px: 2
                      }}>
                        {t("Download Specification Document")}
                      </Button>
                    )}
                    {safetyDocUrl && (
                      <Button variant="outlined" startIcon={<LuFile size={18} />} endIcon={<LuDownload size={16} />} href={safetyDocUrl} target="_blank" download="safety_document" sx={{
                        textTransform: "none", borderColor: GREEN, color: GREEN, "&:hover": { borderColor: "#118c3c", color: "#118c3c" }, borderRadius: "8px", py: 1.25, justifyContent: "space-between", px: 2
                      }}>
                        {t("Download Product Safety (SDS)")}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            )}
          </Box>
        );
      case "scope":
        return (
          <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: "16px", p: 3, backgroundColor: tok.bg.card }}>
            <Typography variant="h6" sx={{ fontFamily: tok.font.family, fontWeight: 600, color: tok.text.primary, mb: 2 }}>
              {t("Scope & Goals")}
            </Typography>
            <KVTable
              isLinkColumn={isScopeLink}
              rows={[
                [t("User Type / Role"), entity?.role || "LCA Practitioner"],
                [t("Organizational Boundary"), entity?.org || "Operational control"],
                [t("Goals of Assessment"), entity?.goals || "N/A"],
                [t("Assumptions & Cut-offs"), entity?.assumptions || "N/A"],
                [t("Declared Unit"), lcaInfo?.declared_unit || entity?.functional_unit || "1 unit"],
                [t("Reference PCR"), lcaInfo?.reference_pcr || entity?.pcr_link || "N/A"],
                [t("LCA Software"), lcaInfo?.lca_software || "N/A"],
                [t("LCI Database"), lcaInfo?.lci_database || "N/A"],
                [t("LCIA Methodology"), lcaInfo?.lcia_methodology || "N/A"],
                [t("Cut-Off rule"), lcaInfo?.cutoff || "N/A"],
                [t("Allocation Procedure"), lcaInfo?.allocation_procedure || "N/A"],
                [t("System Boundary"), entity?.system_boundary || "Cradle-to-gate (A1-A3)"],
                [t("PCR Document Link"), entity?.pcr_link || "N/A"],
                [t("EPD Document Link"), entity?.epd_link || "N/A"],
              ]}
            />
          </Paper>
        );
      case "lci":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* LCI Grid Card */}
            <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: "16px", p: 3, backgroundColor: tok.bg.card }}>
              <Typography variant="h6" sx={{ fontFamily: tok.font.family, fontWeight: 600, color: tok.text.primary, mb: 2 }}>
                {t("LCI Processes")}
              </Typography>
              <Box sx={{ my: 1, mx: -3 }}>
                <AgGrid
                  columnDefs={processGridColDefs}
                  rowData={processes}
                  getRowId={(params) => String(params.data.id)}
                  domLayout="autoHeight"
                  suppressPagination
                  headerCheckbox={false}
                  checkboxes={false}
                />
              </Box>
            </Paper>

            {/* Separate Summary Container - Left-Aligned */}
            <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: "16px", p: 3, backgroundColor: tok.bg.card }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, textAlign: "left" }}>
                <Typography sx={{ fontFamily: tok.font.family, fontSize: "15px", fontWeight: 600, color: tok.text.primary, textAlign: "left" }}>
                  {t("Process Summary Statement:")}
                </Typography>
                <ul style={{ paddingLeft: "20px", margin: 0, textAlign: "left" }}>
                  <li style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", color: tok.text.secondary, marginBottom: "8px", textAlign: "left" }}>
                    {t(`For the mapped life cycle processes, the total Global Warming Potential (fossil GWP) is ${footprint.fossilGwp.toFixed(2)} kg CO₂e.`)}
                  </li>
                  <li style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", color: tok.text.secondary, marginBottom: "8px", textAlign: "left" }}>
                    {t(`The total biogenic carbon (biogenic GWP) is ${footprint.biogenicGwp.toFixed(2)} kg CO₂e.`)}
                  </li>
                  <li style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", color: tok.text.secondary, marginBottom: "8px", textAlign: "left" }}>
                    {t(`The net cumulative impact results in a total carbon footprint of ${footprint.netGwp.toFixed(2)} kg CO₂e.`)}
                  </li>
                </ul>
              </Box>
            </Paper>
          </Box>
        );
      case "env_factors":
        return (
          <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: "16px", p: 3, backgroundColor: tok.bg.card }}>
            <Typography variant="h6" sx={{ fontFamily: tok.font.family, fontWeight: 600, color: tok.text.primary }}>
              {t("Environmental Factors")}
            </Typography>
            <Box sx={{ my: 1, mx: -3 }}>
              <AgGrid
                columnDefs={envFactorColDefs}
                rowData={localEnvFactors}
                getRowId={(params) => String(params.data.id)}
                domLayout="autoHeight"
                suppressPagination
                headerCheckbox={false}
                checkboxes={false}
              />
            </Box>
          </Paper>
        );
      case "results":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }} className="report-preview-pane">
            <Footprint processData={processes} pageType={type === "Product" ? "supplier" : "utilizer"} />
            <SystemBoundary processData={processes} isEpd={entity?.is_epd} />
          </Box>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", height: "100%", width: "100%", py: 10 }}>
        <CircularProgress sx={{ color: GREEN }} />
        <Typography sx={{ mt: 2, color: tok.text.muted, fontFamily: tok.font.family }}>{t("Compiling report data...")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2, height: "100%" }}>
      <style>{`
        .report-preview-pane canvas {
          max-height: 250px !important;
          max-width: 100% !important;
        }
        .report-preview-pane .sankey-container {
          max-height: 300px !important;
          overflow: auto;
        }
        .report-preview-pane .system-boundary-container {
          max-width: 100% !important;
          width: 100% !important;
          overflow-x: auto !important;
        }
      `}</style>

      {/* Title header bar */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5,
        borderRadius: "16px", border: `1px solid ${tok.border.card}`, backgroundColor: tok.bg.card, p: 2,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ textAlign: "left" }}>
            <Typography sx={{ fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family, fontSize: "18px" }}>
              {t("LCA Assessment Report")}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" startIcon={<LuBookOpen size={16} />} onClick={() => setFullMode((v) => !v)} sx={{
            textTransform: "none", borderRadius: tok.radius.input, borderColor: tok.border.card, color: tok.text.primary,
            fontSize: tok.font.size.footerLabel, fontFamily: tok.font.family, fontWeight: 500,
          }}>
            {fullMode ? t("Section view") : t("Full Report")}
          </Button>
          <Button variant="contained" startIcon={<LuFileDown size={16} />} onClick={handleDocxExport} sx={{
            textTransform: "none", borderRadius: "8px", backgroundColor: GREEN, fontSize: "13px", fontWeight: 500,
            boxShadow: "none", "&:hover": { backgroundColor: "#118c3c", boxShadow: "none" }, color: "white",
          }}>{t("Download Word")}</Button>
        </Box>
      </Box>

      {/* Main interactive side sections */}
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "320px 1fr" }, flex: 1 }}>
        <PanelCard title={t("Report Contents")} tok={tok}>
          <Box component="ol" sx={{ display: "grid", gap: 0.75, m: 0, p: 0, listStyle: "none" }}>
            {sectionList.map((s, i) => {
              const Icon = s.icon;
              const active = s.key === selected;
              const dot = s.status === "complete" ? "#10b981" : s.status === "partial" ? "#f59e0b" : "#d4d4d4";
              return (
                <Box component="li" key={s.key} sx={{ listStyle: "none" }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setSelected(s.key)}
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1.25, width: "100%", textAlign: "left",
                      border: `1px solid ${active ? "rgba(21,164,72,0.4)" : tok.border.card}`,
                      backgroundColor: active ? (isDark ? "rgba(21,164,72,0.15)" : "rgba(21,164,72,0.10)") : "transparent",
                      borderRadius: "6px", px: 1.5, py: 1, cursor: "pointer", fontFamily: tok.font.family,
                      "&:hover": { backgroundColor: active ? undefined : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)") },
                    }}
                  >
                    <Box sx={{ width: 24, height: 24, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: "11px", fontWeight: 600, flexShrink: 0, backgroundColor: active ? GREEN : (isDark ? "rgba(21,164,72,0.15)" : "rgba(21,164,72,0.12)"), color: active ? "#fff" : GREEN }}>
                      {i + 1}
                    </Box>
                    <Icon size={16} color={GREEN} style={{ flexShrink: 0 }} />
                    <Typography sx={{ flex: 1, fontSize: tok.font.size.bodyText, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{t(s.label)}</Typography>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: dot, flexShrink: 0 }} title={s.status} />
                    <LuChevronRight size={16} color={active ? GREEN : tok.text.muted} style={{ flexShrink: 0 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </PanelCard>

        {/* Content Preview without redundant double container Paper wrapper */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {fullMode ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {sectionList.map((sec, idx) => (
                <Box key={sec.key}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: "12px", fontWeight: 600, backgroundColor: GREEN, color: "#fff" }}>
                      {idx + 1}
                    </Box>
                    <Typography variant="h6" sx={{ fontFamily: tok.font.family, fontWeight: 600, color: tok.text.primary }}>
                      {t(sec.label)}
                    </Typography>
                  </Box>
                  {renderContent(sec.key)}
                  {idx < sectionList.length - 1 && <Divider sx={{ mt: 4 }} />}
                </Box>
              ))}
            </Box>
          ) : (
            renderContent(selected)
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WorkspaceReportPanel;
